import React, { useState } from 'react';
import {
  DollarSign,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Loader2,
  RefreshCw,
  Cpu,
  Wrench,
  Activity,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import AdminLayout from './AdminLayout';
import { AIVIAUsageSection } from '@/components/admin/AIVIAUsageSection';
import { useAuth } from '@/context/AuthContext';
import { isSystemAdminRole } from '@/lib/roles';
import { apiRequest } from '@/lib/queryClient';

type TimeRange = '24h' | '7d' | '30d';

interface CostDashboardData {
  timestamp: string;
  timeRange: TimeRange;
  rangeStart: string;
  rangeEnd: string;
  summary: {
    totalCostUsd: number;
    averageCostPerRequest: number;
    totalExecutions: number;
    successfulExecutions: number;
    failedExecutions: number;
  };
  costByProvider: Record<string, number>;
  costByTool: Array<{
    toolName: string;
    totalCostUsd: number;
    invocations: number;
    successRate: number;
    averageLatencyMs: number | null;
  }>;
  pricing: {
    currency: string;
    providers: Record<string, Record<string, { input: number; output: number }>>;
  };
  budgetConfig: {
    maxCostPerRequest: number;
    enabled: boolean;
  };
  sloStatus: {
    costPerRequestThreshold: number | null;
    currentAverageCost: number;
    withinThreshold: boolean;
    violations: Array<{
      metric: string;
      actual: number;
      target: number;
      severity: string;
    }>;
  };
}

const TIME_RANGE_LABELS: Record<TimeRange, string> = {
  '24h': 'Last 24 hours',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
};

const formatCurrency = (value: number, currency = 'USD') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 4,
    maximumFractionDigits: 6,
  }).format(value);
};

const formatPercent = (value: number) => {
  return `${(value * 100).toFixed(1)}%`;
};

const formatLatency = (ms: number | null) => {
  if (ms === null) return '—';
  if (ms < 1000) return `${ms.toFixed(0)}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
};

const PROVIDER_COLORS: Record<string, string> = {
  ANTHROPIC: 'bg-orange-500',
  OPENAI: 'bg-emerald-500',
  GOOGLE: 'bg-blue-500',
};

const CostDashboardPage = () => {
  const { user } = useAuth();
  const isSystemAdmin = isSystemAdminRole(user?.role);
  const [timeRange, setTimeRange] = useState<TimeRange>('24h');

  const {
    data,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useQuery<CostDashboardData>({
    queryKey: ['cost-dashboard', timeRange],
    queryFn: () => apiRequest('GET', `/api/metrics/cost-dashboard?range=${timeRange}`),
    staleTime: 30_000,
    enabled: isSystemAdmin,
  });

  if (!isSystemAdmin) {
    return (
      <AdminLayout
        title="Cost Dashboard"
        description="Access denied"
      >
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Access Denied</AlertTitle>
          <AlertDescription>
            You must be a System Administrator to view cost metrics.
          </AlertDescription>
        </Alert>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title="Cost Dashboard"
      description="Monitor AI orchestration costs, provider spending, and budget compliance."
    >
      <div className="space-y-6">
        {/* Controls */}
        <div className="flex items-center justify-between">
          <Select
            value={timeRange}
            onValueChange={(v) => setTimeRange(v as TimeRange)}
          >
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(TIME_RANGE_LABELS).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            {isFetching ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Refresh
          </Button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
          </div>
        ) : error ? (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Error loading cost data</AlertTitle>
            <AlertDescription>
              {error instanceof Error ? error.message : 'Unknown error'}
            </AlertDescription>
          </Alert>
        ) : data ? (
          <>
            {/* SLO Status Alert */}
            {!data.sloStatus.withinThreshold && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Cost SLO Violation</AlertTitle>
                <AlertDescription>
                  Average cost per request ({formatCurrency(data.sloStatus.currentAverageCost)})
                  exceeds threshold ({formatCurrency(data.sloStatus.costPerRequestThreshold ?? 0)}).
                </AlertDescription>
              </Alert>
            )}

            {/* Summary Cards */}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <SummaryCard
                title="Total Cost"
                value={formatCurrency(data.summary.totalCostUsd)}
                icon={DollarSign}
                description={`${TIME_RANGE_LABELS[timeRange]}`}
                highlight
              />
              <SummaryCard
                title="Avg Cost / Request"
                value={formatCurrency(data.summary.averageCostPerRequest)}
                icon={TrendingUp}
                description={`Budget: ${formatCurrency(data.budgetConfig.maxCostPerRequest)}`}
                status={
                  data.sloStatus.withinThreshold ? 'success' : 'warning'
                }
              />
              <SummaryCard
                title="Total Executions"
                value={data.summary.totalExecutions.toLocaleString()}
                icon={Activity}
                description={`${data.summary.successfulExecutions} successful`}
              />
              <SummaryCard
                title="Success Rate"
                value={
                  data.summary.totalExecutions > 0
                    ? formatPercent(
                        data.summary.successfulExecutions /
                          data.summary.totalExecutions,
                      )
                    : '—'
                }
                icon={CheckCircle}
                description={`${data.summary.failedExecutions} failed`}
              />
            </div>

            {/* Cost by Provider */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Cpu className="h-5 w-5" />
                  Cost by Provider
                </CardTitle>
                <CardDescription>
                  Spending breakdown across AI providers
                </CardDescription>
              </CardHeader>
              <CardContent>
                {Object.keys(data.costByProvider).length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No provider cost data available for this period.
                  </p>
                ) : (
                  <div className="space-y-4">
                    {Object.entries(data.costByProvider)
                      .sort(([, a], [, b]) => b - a)
                      .map(([provider, cost]) => {
                        const percent =
                          data.summary.totalCostUsd > 0
                            ? (cost / data.summary.totalCostUsd) * 100
                            : 0;
                        return (
                          <div key={provider} className="space-y-2">
                            <div className="flex items-center justify-between text-sm">
                              <div className="flex items-center gap-2">
                                <div
                                  className={`h-3 w-3 rounded-full ${PROVIDER_COLORS[provider] ?? 'bg-gray-400'}`}
                                />
                                <span className="font-medium">{provider}</span>
                              </div>
                              <div className="flex items-center gap-4">
                                <span className="text-muted-foreground">
                                  {percent.toFixed(1)}%
                                </span>
                                <span className="font-semibold">
                                  {formatCurrency(cost)}
                                </span>
                              </div>
                            </div>
                            <Progress value={percent} className="h-2" />
                          </div>
                        );
                      })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Tool Performance */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wrench className="h-5 w-5" />
                  Tool Performance
                </CardTitle>
                <CardDescription>
                  Per-tool performance metrics and success rates
                </CardDescription>
              </CardHeader>
              <CardContent>
                {data.costByTool.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No tool performance data available for this period.
                  </p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Tool</TableHead>
                        <TableHead className="text-right">Invocations</TableHead>
                        <TableHead className="text-right">Success Rate</TableHead>
                        <TableHead className="text-right">Avg Latency</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.costByTool.map((tool) => (
                        <TableRow key={tool.toolName}>
                          <TableCell className="font-medium">
                            {tool.toolName}
                          </TableCell>
                          <TableCell className="text-right">
                            {tool.invocations.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right">
                            <Badge
                              variant={
                                tool.successRate >= 0.95
                                  ? 'default'
                                  : tool.successRate >= 0.8
                                    ? 'secondary'
                                    : 'destructive'
                              }
                            >
                              {formatPercent(tool.successRate)}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right text-muted-foreground">
                            {formatLatency(tool.averageLatencyMs)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {/* Pricing Configuration */}
            <Card>
              <CardHeader>
                <CardTitle>Current Pricing Configuration</CardTitle>
                <CardDescription>
                  Model pricing rates (per million tokens) used for cost calculations
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-6 md:grid-cols-3">
                  {Object.entries(data.pricing.providers).map(
                    ([provider, models]) => (
                      <div key={provider} className="space-y-3">
                        <h4 className="flex items-center gap-2 font-semibold">
                          <div
                            className={`h-3 w-3 rounded-full ${PROVIDER_COLORS[provider] ?? 'bg-gray-400'}`}
                          />
                          {provider}
                        </h4>
                        <div className="space-y-2 text-sm">
                          {Object.entries(models).map(([model, pricing]) => (
                            <div
                              key={model}
                              className="flex items-center justify-between rounded-md bg-muted/50 px-3 py-2"
                            >
                              <span className="font-mono text-xs text-muted-foreground">
                                {model}
                              </span>
                              <div className="text-right text-xs">
                                <div>
                                  In: ${pricing.input.toFixed(2)}/M
                                </div>
                                <div>
                                  Out: ${pricing.output.toFixed(2)}/M
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ),
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Budget Configuration */}
            <Card>
              <CardHeader>
                <CardTitle>Budget Configuration</CardTitle>
                <CardDescription>
                  Current request budget and SLO settings
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 sm:grid-cols-3">
                  <div className="rounded-lg border p-4">
                    <p className="text-sm text-muted-foreground">
                      Max Cost per Request
                    </p>
                    <p className="mt-1 text-2xl font-semibold">
                      {formatCurrency(data.budgetConfig.maxCostPerRequest)}
                    </p>
                  </div>
                  <div className="rounded-lg border p-4">
                    <p className="text-sm text-muted-foreground">
                      Budget Enforcement
                    </p>
                    <p className="mt-1 text-2xl font-semibold">
                      {data.budgetConfig.enabled ? (
                        <Badge variant="default">Enabled</Badge>
                      ) : (
                        <Badge variant="secondary">Disabled</Badge>
                      )}
                    </p>
                  </div>
                  <div className="rounded-lg border p-4">
                    <p className="text-sm text-muted-foreground">
                      Cost SLO Threshold
                    </p>
                    <p className="mt-1 text-2xl font-semibold">
                      {data.sloStatus.costPerRequestThreshold !== null
                        ? formatCurrency(data.sloStatus.costPerRequestThreshold)
                        : 'Not set'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Last Updated */}
            <p className="text-center text-xs text-muted-foreground">
              Data from {new Date(data.rangeStart).toLocaleString()} to{' '}
              {new Date(data.rangeEnd).toLocaleString()}
            </p>
          </>
        ) : null}
        <AIVIAUsageSection range={timeRange} />
      </div>
    </AdminLayout>
  );
};

interface SummaryCardProps {
  title: string;
  value: string;
  icon: React.ElementType;
  description?: string;
  highlight?: boolean;
  status?: 'success' | 'warning' | 'error';
}

const SummaryCard = ({
  title,
  value,
  icon: Icon,
  description,
  highlight,
  status,
}: SummaryCardProps) => {
  const statusColors = {
    success: 'text-emerald-600 dark:text-emerald-400',
    warning: 'text-amber-600 dark:text-amber-400',
    error: 'text-red-600 dark:text-red-400',
  };

  return (
    <Card
      className={
        highlight
          ? 'border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-900/30'
          : ''
      }
    >
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p
              className={`mt-2 text-2xl font-bold ${status ? statusColors[status] : ''}`}
            >
              {value}
            </p>
            {description && (
              <p className="mt-1 text-xs text-muted-foreground">{description}</p>
            )}
          </div>
          <div
            className={`rounded-full p-2 ${highlight ? 'bg-orange-100 text-orange-600 dark:bg-orange-800 dark:text-orange-400' : 'bg-muted text-muted-foreground'}`}
          >
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CostDashboardPage;
